import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Spin } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  UserSwitchOutlined,
  CreditCardOutlined,
  ShopOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { customerAuthService } from '../services/customerAuth';

const { Header, Sider, Content } = AntLayout;

export const CustomerLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const customer = customerAuthService.getCurrentCustomer();
  const isAuthenticated = customerAuthService.isAuthenticated();
  const mustChangePassword = customerAuthService.mustChangePassword();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
    } else if (mustChangePassword && location.pathname !== '/customer/change-password' && location.pathname !== '/customer/login') {
      navigate('/customer/change-password');
    }
  }, [isAuthenticated, mustChangePassword, navigate, location.pathname]);

  if (!isAuthenticated) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  const menuItems = [
    {
      key: '/customer/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/customer/files',
      icon: <FileOutlined />,
      label: 'File',
    },
    {
      type: 'group' as const,
      label: 'Customer',
      children: [
        {
          key: '/customer/personal',
          icon: <UserSwitchOutlined />,
          label: 'Personal',
        },
        {
          key: '/customer/business',
          icon: <ShopOutlined />,
          label: 'Business',
        },
      ],
    },
    {
      type: 'group' as const,
      label: 'Billing',
      children: [
        {
          key: '/customer/billing',
          icon: <CreditCardOutlined />,
          label: 'Billing',
        },
      ],
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const menuKeys = menuItems.flatMap((item: any) => {
    if (item.children) {
      return item.children.map((child: any) => child.key).filter(Boolean);
    }
    return item.key ? [item.key] : [];
  });

  const selectedMenuKey =
    menuKeys
      .filter((key) => location.pathname === key || location.pathname.startsWith(`${key}/`))
      .sort((a, b) => b.length - a.length)[0] || location.pathname;

  const handleLogout = () => {
    customerAuthService.logout();
    navigate('/customer/login');
  };

  const userMenuItems = [
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible
        collapsed={collapsed}
        collapsedWidth={80}
        className="app-sider"
        width={240}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <div
          className="app-sider-logo"
          style={{ 
          height: 32, 
          margin: 16, 
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600
        }}>
          {collapsed ? 'BFS' : 'Byte Flow Soution'}
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[selectedMenuKey]}
          items={menuItems}
          onClick={handleMenuClick}
          className="app-menu"
        />
      </Sider>
      <AntLayout style={{ marginLeft: collapsed ? 80 : 240 }}>
        <Header style={{ 
          padding: 0, 
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingRight: 24,
          top: 0,
          zIndex: 1,
        }}>
          <button
            type="button"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={() => setCollapsed(!collapsed)}
            className="app-sider-toggle"
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            arrow
          >
            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} />
              <span style={{ marginLeft: 8 }}>{customer?.name || 'Customer'}</span>
            </div>
          </Dropdown>
        </Header>
        <Content
          style={{
            margin: '24px 16px',
            padding: 24,
            minHeight: 280,
            background: '#fff',
            borderRadius: 8,
          }}
        >
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};
